import { useQueryData } from "@src/hooks/useQueryData";
import { byCode, reloadAndReplaceCachedListItem } from "@src/hooks/queryListCache";
import {
    storePlacesServices,
    type StockPlacementContent,
    type StockPlacementContentExtends,
} from "../../../../../api/storePlaces";

/** Ключ query состава приходного ордера для размещения ТМЦ. */
export function getStockPlacementContentsQueryKey(orderCode: number) {
    return ["stock", "placement", orderCode, "contents"] as const;
}

/** Связи строк состава ПО для списка размещения ТМЦ. */
export const STOCK_PLACEMENT_CONTENTS_INCLUDES = {
    include: [
        // "DepotCard",
        "StockObj",
        "Measure",
        "Lot",
        "RecommendedCells",
        "SpMovings",
    ] satisfies Array<keyof StockPlacementContentExtends>,
} as const;

/** Результат загрузки состава приходного ордера. */
export interface UseStockPlacementContentsQueryResult {
    /** Строки состава. */
    content: StockPlacementContent[];
    /** Признак фоновой загрузки. */
    isFetching: boolean;
    /** Перезагружает весь состав приходного ордера. */
    refetch: () => Promise<unknown>;
    /**
     * Перезагружает одну строку состава и заменяет её в query cache.
     * @param contentCode код строки состава ПО
     */
    refetchContentByCode: (contentCode: number) => Promise<void>;
}

/** Опции загрузки состава приходного ордера. */
interface UseStockPlacementContentsQueryOptions {
    /** Выполнять ли refetch при монтировании observer-а для stale-данных. */
    refetchOnMount?: boolean;
}

/** Загружает состав приходного ордера для размещения ТМЦ. */
export function useStockPlacementContentsQuery(
    orderCode: number,
    options?: UseStockPlacementContentsQueryOptions,
): UseStockPlacementContentsQueryResult {
    const query = useQueryData({
        queryKey: getStockPlacementContentsQueryKey(orderCode),
        queryFn: async ({ signal }) => {
            const items = await storePlacesServices.content.loadForPlacement(
                {
                    flt: { StockOrderCode: orderCode },
                    ...STOCK_PLACEMENT_CONTENTS_INCLUDES,
                },
                { signal },
            );
            return items;
        },
        enabled: orderCode > 0,
        refetchOnMount: options?.refetchOnMount,
        meta: { errorSource: "stock.placement.contents" },
    });

    const refetchContentByCode = async (contentCode: number): Promise<void> => {
        await reloadAndReplaceCachedListItem({
            setData: query.setData,
            getKey: byCode,
            loadItem: () =>
                storePlacesServices.content.loadForPlacementByCode({
                    code: contentCode,
                    orderCode,
                    ...STOCK_PLACEMENT_CONTENTS_INCLUDES,
                }),
        });
    };

    return {
        content: query.data ?? [],
        isFetching: query.isFetching,
        refetch: query.refetch,
        refetchContentByCode,
    };
}
